import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Button, 
  CircularProgress, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import { 
  Add as AddIcon, 
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { playerService, Player, User, FantasyTeam, FantasyTeamPlayer, FantasyLeague } from '../services/playerService';
import { useTheme } from '@mui/material/styles';

const Item = ({ children }: { children: React.ReactNode }) => (
  <div style={{ padding: '8px', textAlign: 'center' }}>{children}</div>
);

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const { currentUsername, isUserSwitching, currentUser, setCurrentUser, setCurrentUsername } = useUser();
  const [userFantasyTeams, setUserFantasyTeams] = useState<FantasyTeam[]>([]);
  const [userFantasyLeagues, setUserFantasyLeagues] = useState<FantasyLeague[]>([]);
  const [allFantasyLeagues, setAllFantasyLeagues] = useState<FantasyLeague[]>([]);
  const [userPlayers, setUserPlayers] = useState<FantasyTeamPlayer[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    email: ''
  });
  const [newTeamForm, setNewTeamForm] = useState({
    name: '',
    leagueId: ''
  });
  const [deleteTeamDialogOpen, setDeleteTeamDialogOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<FantasyTeam | null>(null);
  const [removeUsername, setRemoveUsername] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [renameTeamId, setRenameTeamId] = useState<string>('');
  const [renameTeamName, setRenameTeamName] = useState('');
  const [scheduleLeagueId, setScheduleLeagueId] = useState<string>('');

  // Memoize the loadSettingsData function to prevent recreation on every render
  const loadSettingsData = useCallback(async () => {
    if (!currentUsername) return;
    
    try {
      setLoading(true);
      setError(null);

      console.log(`🔄 SettingsPage: Loading data for user "${currentUsername}"...`);
      
      // Load user by username
      const user = await playerService.getUserByUsername(currentUsername);
      console.log('✅ SettingsPage: User loaded:', user);
      setCurrentUser(user);
      setNewUsername(user.username);
      setFirstName(user.firstName ?? '');
      setLastName(user.lastName ?? '');
      setScheduleLeagueId(prev => prev || userFantasyLeagues[0]?.id?.toString() || '');

      // Load user's fantasy teams
      const fantasyTeams = await playerService.getFantasyTeamsByUser(user.id);
      console.log('✅ SettingsPage: Fantasy teams loaded:', fantasyTeams);
      setUserFantasyTeams(fantasyTeams);
      if (fantasyTeams.length > 0) {
        const firstTeamId = fantasyTeams[0].id.toString();
        setRenameTeamId(firstTeamId);
        setRenameTeamName(fantasyTeams[0].name);
      } else {
        setRenameTeamId('');
        setRenameTeamName('');
      }

      // Load user's fantasy leagues
      const fantasyLeagues = await playerService.getFantasyLeaguesByUser(user.id);
      console.log('✅ SettingsPage: Fantasy leagues loaded:', fantasyLeagues);
      setUserFantasyLeagues(fantasyLeagues);
      if (!scheduleLeagueId && fantasyLeagues.length > 0) {
        setScheduleLeagueId(fantasyLeagues[0].id.toString());
      }

      const allLeagues = await playerService.getFantasyLeagues();
      console.log('✅ SettingsPage: All fantasy leagues loaded:', allLeagues);
      setAllFantasyLeagues(allLeagues);

      // Load all players for settings context
      const allPlayersData = await playerService.getAllPlayers();
      console.log('✅ SettingsPage: All players loaded:', allPlayersData.length);
      setAllPlayers(allPlayersData);

      // Load all users for the remove-user dropdown
      const allUsersData = await playerService.getAllUsers();
      console.log('✅ SettingsPage: All users loaded:', allUsersData.length);
      setAllUsers(allUsersData);
      if (allUsersData.length > 0 && !allUsersData.some(u => u.username === removeUsername)) {
        setRemoveUsername(allUsersData[0].username);
      } else if (allUsersData.length === 0) {
        setRemoveUsername('');
      }

    } catch (err) {
      console.error('❌ SettingsPage: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load settings data');
    } finally {
      setLoading(false);
    }
  }, [currentUsername]);

  // Load data when component mounts or when user changes
  useEffect(() => {
    if (currentUsername && !isUserSwitching) {
      loadSettingsData();
    }
  }, [currentUsername, isUserSwitching, loadSettingsData]);

  // Keep remove user select in range
  useEffect(() => {
    if (allUsers.length === 0) {
      setRemoveUsername('');
      return;
    }
    if (!allUsers.some(u => u.username === removeUsername)) {
      setRemoveUsername(allUsers[0].username);
    }
  }, [allUsers, removeUsername]);

  // Handle create team
  const handleCreateTeam = async () => {
    if (!currentUser || !newTeamForm.name || !newTeamForm.leagueId) {
      setSnackbarMessage('Please fill in all required fields');
      setSnackbarOpen(true);
      return;
    }

    try {
      const newTeam = await playerService.createFantasyTeam({
        name: newTeamForm.name,
        ownerId: currentUser.id,
        leagueId: parseInt(newTeamForm.leagueId)
      });
      
      setUserFantasyTeams([...userFantasyTeams, newTeam]);
      setNewTeamForm({ name: '', leagueId: '' });
      setSnackbarMessage(`Team "${newTeam.name}" created successfully!`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error creating team:', err);
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to create team');
      setSnackbarOpen(true);
    }
  };

  // Handle delete team
  const handleDeleteTeam = async (team: FantasyTeam) => {
    if (!team) return;

    try {
      await playerService.deleteFantasyTeam(team.id);
      setUserFantasyTeams(userFantasyTeams.filter(t => t.id !== team.id));
      setDeleteTeamDialogOpen(false);
      setTeamToDelete(null);
      setSnackbarMessage(`Team "${team.name}" deleted successfully!`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error deleting team:', err);
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to delete team');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteClick = (team: FantasyTeam) => {
    setTeamToDelete(team);
    setDeleteTeamDialogOpen(true);
  };

  const handleUpdateUserName = async () => {
    if (!currentUser) return;
    if (!newUsername && !firstName && !lastName) {
      setSnackbarMessage('Enter a new username or name to update');
      setSnackbarOpen(true);
      return;
    }

    try {
      const updated = await playerService.updateUserName(currentUsername, {
        newUsername: newUsername || undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined
      });
      setCurrentUser(updated);
      if (newUsername && newUsername !== currentUsername) {
        setCurrentUsername(newUsername);
      }
      setSnackbarMessage('User details updated successfully!');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error updating user:', err);
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to update user');
      setSnackbarOpen(true);
    }
  };

  const handleGenerateSchedule = async () => {
    if (!scheduleLeagueId) {
      setSnackbarMessage('Select a league to generate a schedule');
      setSnackbarOpen(true);
      return;
    }
    try {
      setLoading(true);
      await playerService.generateSchedule(parseInt(scheduleLeagueId, 10));
      setSnackbarMessage('Schedule generated successfully');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error generating schedule:', err);
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to generate schedule');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!scheduleLeagueId) {
      setSnackbarMessage('Select a league to delete the schedule');
      setSnackbarOpen(true);
      return;
    }
    try {
      setLoading(true);
      await playerService.deleteSchedule(parseInt(scheduleLeagueId, 10));
      setSnackbarMessage('Schedule deleted');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to delete schedule');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRenameTeam = async () => {
    if (!renameTeamId || !renameTeamName.trim()) {
      setSnackbarMessage('Please select a team and enter a new name');
      setSnackbarOpen(true);
      return;
    }

    try {
      const updatedTeam = await playerService.updateFantasyTeamName(parseInt(renameTeamId), renameTeamName.trim());
      // Update the team in the list
      setUserFantasyTeams(userFantasyTeams.map(team => 
        team.id === updatedTeam.id ? updatedTeam : team
      ));
      setSnackbarMessage(`Team renamed to "${updatedTeam.name}" successfully!`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error renaming team:', err);
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to rename team');
      setSnackbarOpen(true);
    }
  };

  // Handle create user
  const handleCreateUser = async () => {
    if (!newUserForm.username || !newUserForm.email) {
      setSnackbarMessage('Please provide a username and email');
      setSnackbarOpen(true);
      return;
    }

    try {
      const user = await playerService.createUser({
        username: newUserForm.username,
        email: newUserForm.email,
        firstName: '',
        lastName: ''
      });

      setNewUserForm({ username: '', email: '' });
      setSnackbarMessage(`User "${user.username}" created successfully!`);
      setSnackbarOpen(true);
      await loadSettingsData();
    } catch (err) {
      console.error('Error creating user:', err);
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to create user');
      setSnackbarOpen(true);
    }
  };

  // Handle remove user
  const handleRemoveUser = async () => {
    if (!removeUsername) {
      setSnackbarMessage('Please enter a username to remove');
      setSnackbarOpen(true);
      return;
    }

    try {
      await playerService.deleteUserByUsername(removeUsername);
      setSnackbarMessage(`User "${removeUsername}" removed successfully!`);
      setSnackbarOpen(true);
      setRemoveUsername('');
      setAllUsers(allUsers.filter(u => u.username !== removeUsername));

      // Clear context if the current user was deleted
      if (removeUsername === currentUsername) {
        setCurrentUser(null);
      }

      await loadSettingsData();
    } catch (err) {
      console.error('Error removing user:', err);
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to remove user');
      setSnackbarOpen(true);
    }
  };

  // Show loading state while user is switching
  if (isUserSwitching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Switching to {currentUsername}...
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Grid size={12}>
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>🏀 Team Management</Typography>
            
            {/* Create Team Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Create Team
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Team Name"
                    variant="outlined"
                    value={newTeamForm.name}
                    onChange={(e) => setNewTeamForm({ ...newTeamForm, name: e.target.value })}
                  />
                </Grid>
                <Grid size={6}>
                  <FormControl fullWidth>
                    <InputLabel>League</InputLabel>
                    <Select
                      value={newTeamForm.leagueId}
                      label="League"
                      onChange={(e) => setNewTeamForm({ ...newTeamForm, leagueId: e.target.value })}
                    >
                      {allFantasyLeagues.map((league) => (
                        <MenuItem key={league.id} value={league.id.toString()}>
                          {league.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Button 
                fullWidth 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleCreateTeam}
                disabled={!newTeamForm.name || !newTeamForm.leagueId || allFantasyLeagues.length === 0}
              >
                Create Team
              </Button>
              {allFantasyLeagues.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  You need to be part of a league to create a team
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Rename Team Section */}
            {userFantasyTeams.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                  Rename Team
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={6}>
                    <FormControl fullWidth>
                      <InputLabel>Select Team</InputLabel>
                      <Select
                        value={renameTeamId}
                        label="Select Team"
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          setRenameTeamId(selectedId);
                          const selectedTeam = userFantasyTeams.find(t => t.id.toString() === selectedId);
                          if (selectedTeam) {
                            setRenameTeamName(selectedTeam.name);
                          }
                        }}
                      >
                        {userFantasyTeams.map((team) => (
                          <MenuItem key={team.id} value={team.id.toString()}>
                            {team.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      label="New Team Name"
                      variant="outlined"
                      value={renameTeamName}
                      onChange={(e) => setRenameTeamName(e.target.value)}
                    />
                  </Grid>
                </Grid>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={handleRenameTeam}
                  disabled={!renameTeamId || !renameTeamName.trim()}
                >
                  Rename Team
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Your Teams Section */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Your Teams ({userFantasyTeams.length})
              </Typography>
              {userFantasyTeams.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No teams yet. Create your first team above!
                </Typography>
              ) : (
                <List>
                  {userFantasyTeams.map((team) => (
                    <ListItem
                      key={team.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <ListItemText
                        primary={team.name}
                        secondary={
                          <Box component="span">
                            <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                              League: {team.league?.name || 'Unknown'}
                            </Typography>
                            {team.players && (
                              <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                Players: {team.players.length}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteClick(team)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={6}>
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>👥 User Management</Typography>
            
            {/* Create User Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Create User
              </Typography>
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid size={12}>
                  <Item>
                    <TextField
                      fullWidth
                      label="Username"
                      variant="outlined"
                      value={newUserForm.username}
                      onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                      sx={{ mb: 1 }}
                    />
                  </Item>
                </Grid>
                <Grid size={12}>
                  <Item>
                    <TextField
                      fullWidth
                      label="Email"
                      variant="outlined"
                      type="email"
                      value={newUserForm.email}
                      onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                      sx={{ mb: 1 }}
                    />
                  </Item>
                </Grid>
              </Grid>
              <Button 
                fullWidth 
                variant="contained" 
                startIcon={<PersonAddIcon />}
                onClick={handleCreateUser}
                disabled={!newUserForm.username || !newUserForm.email}
              >
                Create User
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Update User Name Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Update User Name
              </Typography>
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid size={12}>
                  <Item>
                    <TextField
                      fullWidth
                      label="Username"
                      variant="outlined"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                  </Item>
                </Grid>
                <Grid size={12}>
                  <Item>
                    <TextField
                      fullWidth
                      label="First Name"
                      variant="outlined"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                  </Item>
                </Grid>
                <Grid size={12}>
                  <Item>
                    <TextField
                      fullWidth
                      label="Last Name"
                      variant="outlined"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </Item>
                </Grid>
              </Grid>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleUpdateUserName}
                disabled={!currentUser}
              >
                Update Name
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Remove User Section */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Remove User
              </Typography>
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid size={12}>
                  <Item>
                    <FormControl fullWidth sx={{ mb: 1 }}>
                      <InputLabel id="remove-user-label">Select user</InputLabel>
                      <Select
                        labelId="remove-user-label"
                        value={removeUsername}
                        label="Select user"
                        onChange={(e) => setRemoveUsername(e.target.value as string)}
                      >
                        {allUsers.map((user) => (
                          <MenuItem key={user.id} value={user.username}>
                            {user.username}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Item>
                </Grid>
              </Grid>
              <Button 
                fullWidth 
                variant="contained" 
                color="error"
                startIcon={<PersonRemoveIcon />}
              onClick={handleRemoveUser}
              disabled={!removeUsername}
              >
                Remove User
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={12}>
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>🗓 Schedule Management</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={6}>
                <FormControl fullWidth>
                  <InputLabel>League</InputLabel>
                  <Select
                    value={scheduleLeagueId}
                    label="League"
                    onChange={(e) => setScheduleLeagueId(e.target.value)}
                  >
                    {userFantasyLeagues.map((league) => (
                      <MenuItem key={league.id} value={league.id.toString()}>
                        {league.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={6} sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleGenerateSchedule}
                  disabled={!scheduleLeagueId || loading}
                >
                  Generate Schedule
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={handleDeleteSchedule}
                  disabled={!scheduleLeagueId || loading}
                >
                  Delete Schedule
                </Button>
              </Grid>
            </Grid>
            <Typography variant="body2" color="text.secondary">
              Schedules are generated as round-robin matchups. Deleting a schedule removes all scheduled games for the selected league.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Delete Team Confirmation Dialog */}
      <Dialog
        open={deleteTeamDialogOpen}
        onClose={() => {
          setDeleteTeamDialogOpen(false);
          setTeamToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the team "{teamToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteTeamDialogOpen(false);
            setTeamToDelete(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={() => teamToDelete && handleDeleteTeam(teamToDelete)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Grid>
  );
};

export default SettingsPage;
